import App from "./App";
import {createBrowserRouter} from "react-router-dom";
import Matches from "./pages/Matches";
import Competitions from "./pages/Competitions";
import History from "./pages/History";
import Pricing from "./pages/Pricing";
import Checkout from "./pages/Checkout";
import Account from "./pages/Account";
import Auth from "./pages/Auth";
import SideNavigationContext from "./context/SideNavigationContext";
import React, {ReactNode} from "react";
import AnalyzeContext from "./context/AnalyzeContext";
import AuthGuard from "./components/HOC/AuthGuard";
import ErrorPage from "./pages/ErrorPage";
import Landing from "./pages/Landing";

/**
 * Everything behind the analysis features needs an account, the API rejects them otherwise.
 * The guard asks for one in place, so the route is kept and renders as soon as the session lands.
 */
const guarded = (element: ReactNode, reason?: string) => <AuthGuard reason={reason}>{element}</AuthGuard>

export const router = createBrowserRouter([{
    /**
     * What the domain opens on.
     *
     * Outside the app layout: the landing page carries its own header and footer, and is the one
     * screen meant to be read by someone who has no account yet.
     */
    path: "/",
    element: <Landing/>,
    errorElement: <ErrorPage/>
}, {
    path: "/auth",
    element: <AuthGuard reverseAuth>
        <Auth/>
    </AuthGuard>,
    errorElement: <ErrorPage/>
}, {
    path: "",
    element: <SideNavigationContext>
        <AnalyzeContext>
            <App/>
        </AnalyzeContext>
    </SideNavigationContext>,
    // A screen that throws is replaced by the error page rather than a blank one
    errorElement: <ErrorPage/>,
    children: [{
        path: "/analyze",
        element: guarded(<Matches/>, "guardReasonAnalyze")
    }, {
        // Browsing a competition reads the fixture list, which the API only answers for an account
        path: "/competitions",
        element: guarded(<Competitions/>, "guardReasonAnalyze")
    }, {
        path: "/history",
        element: guarded(<History/>, "guardReasonHistory")
    }, {
        path: "/pricing",
        element: <Pricing/>
    }, {
        // The plan is bought for an account, so there has to be one before the payment is asked for
        path: "/checkout",
        element: guarded(<Checkout/>, "guardReasonAccount")
    }, {
        path: "/account",
        element: guarded(<Account/>, "guardReasonAccount")
    }]
}, {
    // Any address without a screen of its own
    path: "*",
    element: <ErrorPage code={404}/>
}])
